import React from 'react'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { CoachAssignment } from '@src/types'
import Intercom from '@src/utils/intercom'
import { CurrentUserQuery } from '@src/graphql/fetchUser.generated'
import { RootStackParamList } from '@src/navigation/types'
import { Coach } from './Coach'

interface CoachProps {
  coachAssignment?: CoachAssignment
  canSwitch: boolean
  conversation?: NonNullable<CurrentUserQuery['currentUser']>['coachChatConversation']
  hasUnreadMessage?: boolean
  showPopularChip: boolean
}

export const MntCard = ({
  coachAssignment,
  conversation,
  hasUnreadMessage = false,
  showPopularChip,
  canSwitch,
}: CoachProps) => {
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()

  if (!coachAssignment || !coachAssignment.coach) {
    return (
      <Coach
        accessibilityLabel="Add nutritionist"
        badgeLabel={showPopularChip ? 'POPULAR' : undefined}
        iconBackgroundStyle={styles.forkIcon}
        iconName="fork-knife"
        onPress={() => navigation.navigate('VideoCallsGettingStartedScreen')}
        subtitle="Personalized Guidance. Lasting Results."
        title="Add Nutritionist"
      />
    )
  }

  const { coach } = coachAssignment
  const { fullName, bio, photo, credentials, specialties } = coach.coachProfile || {}
  const { referralUrl } = coachAssignment
  const displayName = fullName || `${coach.firstName} ${coach.lastName}`

  const handleOpenChat = () => {
    if (!conversation) {
      return
    }

    Intercom.showIntercomConversation(conversation.intercomId)
  }

  const handleOpenBio = () => {
    navigation.navigate('CoachInfoModal', {
      variant: 'assignedCoach',
      photo,
      coachingKind: coachAssignment.coachingKind.key,
      firstName: coach.firstName,
      fullName: displayName,
      subTitle: 'Nutrition Coach',
      bio,
      credentials,
      specialties: specialties || [],
      referralUrl,
      coachAssignmentId: coachAssignment.id,
      showBookVideoCallButton: true,
      showChatAction: true,
      showEditRecurringAction: true,
      showSwitchButton: true,
      canSwitch,
    })
  }

  return (
    <Coach
      accessibilityLabel={`View ${displayName}`}
      hasUnreadMessage={hasUnreadMessage}
      onChatPress={conversation ? handleOpenChat : undefined}
      onPress={handleOpenBio}
      photo={photo}
      subtitle={credentials || 'Nutrition Coach'}
      title={displayName}
    />
  )
}

const themedStyle = StyleService.create({
  forkIcon: {
    backgroundColor: 'theme.accent.citron',
  },
})
